import {
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges
} from '@angular/core';
import { Column, ComparisonConstraint, ConfiguratorTableService, ContentDataByDataviewDTO, DataViewDto, EngineDataQueryService, FieldTableDto, PropertyValue, Table, TableJointure, UpdateData, Value } from '@tj/tj-dmp-lib-api';
import { RawDetailInfoInterface } from '../raw-detail-Info.interface';
import { BuildComponentService } from '../../../../services/build-component.service';
import { FieldService } from "../../../field/field/field.service";
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'dmp-dyn-raw-detail-display',
    templateUrl: './dyn-raw-detail-display.component.html',
    styleUrls: ['./dyn-raw-detail-display.component.scss'],
    providers: [FieldService, BuildComponentService],
    standalone: false
})
export class DynRawDetailDisplayComponent implements OnInit, OnChanges {

    @Input() configCmp: RawDetailInfoInterface

    labelButtonMode: string;
    iconButtonMode: string;
    errors: string[] = [];
    mode: "READ" | "EDIT";
    data: ContentDataByDataviewDTO;
    tables: Table[];
    columnNames: string[] = [];
    isCommandDataview: boolean = false;
    isPersonalDataview: boolean = false;
    isLeadDataview: boolean = false;
    dataLoaded: {[key: string]: boolean} = {};
    private tabMapping: string[];
    private tablesByDataviewType: { [key: string]: string[] } = {};
    
    constructor(
        public fieldService: FieldService,
        private changeDetectorRef: ChangeDetectorRef,
        private dataEngine: EngineDataQueryService,
        private messageService: MessageService,
        private engineDataQueryService: EngineDataQueryService,
        private tableResourceService: ConfiguratorTableService,
        private translate: TranslateService
    ) { }

    ngOnInit(): void {
        const dataviewCode = (this.configCmp.dataView.code || '').toLowerCase();
        const tableName = (this.configCmp.dataView.table?.tableName || '').toLowerCase();

        // Detect context from main table name first (more reliable), fall back to code (case-insensitive)
        this.isCommandDataview = tableName.includes('command') || dataviewCode.includes('command');
        this.isPersonalDataview = tableName.includes('personal') || dataviewCode.includes('personal');
        this.isLeadDataview = dataviewCode.includes('lead');

        this.updateLabelButton();
        this.updateFieldService();
        this.loadData();
        this.getColumnNames();
        this.initTabMapping();

        this.tablesByDataviewType = {
            ["command"]: ['command', 'flight', 'carrental', 'hotel', 'parking'],
            ['personal']: ['personal', 'personal_affilie_' + this.configCmp.partner.toLowerCase()]
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.configCmp) {
            this.updateFieldService();
        }
    }

    private updateFieldService() {
        this.fieldService.dataView = this.configCmp.dataView;
        this.fieldService.datastore = this.configCmp.datastore;
        this.fieldService.partner = this.configCmp.partner;
    }

    private updateLabelButton() {
        if (this.fieldService.mode === 'EDIT') {
            this.iconButtonMode = 'far fa-save';
            this.labelButtonMode = 'Enregistrer';
        } else {
            this.labelButtonMode = 'Modifier';
            this.iconButtonMode = 'far fa-edit';
        }
    }

    private getColumnNames():void{
        const columns: Column[] = this.configCmp.dataView.defaultDisplayFields;
        columns.forEach(col =>
            this.columnNames.push(`${col.tableName}.${col.propertyName}`)
        );
    }

    doEditData() {
        this.fieldService.mode = 'EDIT';
        this.changeDetectorRef.detectChanges();
    }

    doCancelData() {
        this.fieldService.mode = 'READ';
        this.fieldService.getFieldTags().forEach(tag => tag.cancel());
        this.fieldService.getFields()
            .filter(f => f.editor)
            .forEach(f => f.editor.cancel());
        this.changeDetectorRef.detectChanges();
    }

    doSaveData() {
        const modifiedFields = this.fieldService.getFields().filter(f => f.isDirty());
        const validFields = modifiedFields.filter(f => f.isValid());
        const invalidFields = modifiedFields.filter(f => !f.isValid());

        if (invalidFields.length != 0) {
            this.handleValidationErrors(invalidFields);
            return;
        }

        this.fieldService.getFieldTags().forEach(t => t.save());
        const updateField: { [key: string]: string; } = {};
        const idForTable: { [key: string]: number; } = {};

        validFields.forEach(field => {
            updateField[field.getKey()] = field.getValueWithException();
            this.findAndSetTableId(field, idForTable);
        });

        if (this.fieldService.getReassignedCustomer() != null) {
            updateField['command.id_personal'] = this.fieldService.getReassignedCustomer().toString();
            if (idForTable['command.id'] == null) {
                idForTable['command.id'] = this.data.mainData.id
            }
            this.fieldService.reassignCustomer(null);
        }

        if (Object.keys(idForTable).length === 0) {
            this.doCancelData();
            return;
        }
        this.saveData(updateField, idForTable);
    }

    private handleValidationErrors(invalidFields: FieldTableDto[]): void {
        invalidFields.forEach(field => {
            const fieldName = field.label || field.code;
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('components.dyn-raw-detail-display.validationErrorSummary'),
                detail: this.translate.instant('components.dyn-raw-detail-display.invalidFieldDetail', {field: fieldName})
            });
        });
    }

    private findAndSetTableId(field: any, idForTable: { [key: string]: number }): void {
        const key = `${field.getTable()}.id`;
        if (idForTable[key]) return;

        const data = this.data;
        if (!data) return;

        if (this.configCmp.dataView.table.tableName === field.getTable()) {
            idForTable[key] = data.mainData.id;
            return;
        }

        for (const queryKey in data.dataSubQuery) {
            const subQueryData = data.dataSubQuery[queryKey].content[field.indexSubQuery];
            if (subQueryData?.rows[key]) {
                idForTable[key] = subQueryData.rows[key];
                break;
            }
        }
    }

    private saveData(updateField: any, idForTable: any): void {
        const updateData: UpdateData = {
            idDataview: this.configCmp.dataView.id,
            tenant: this.configCmp.datastore,
            partner: this.configCmp.partner,
            updateField,
            idForTable
        };
    
        this.dataEngine.updateData(updateData, this.configCmp.datastore).pipe(
            switchMap(() => {
                const query = this.buildQuery(this.configCmp);
                return this.engineDataQueryService.getQueryDataContentByDataview(
                    query,
                    this.configCmp.datastore,
                    this.configCmp.partner,
                    this.configCmp.dataView.id
                );
            })
        ).subscribe({
            next: (data) => {
                this.data = data;
                this.fieldService.data = data;
                this.fieldService.mode = "READ";
                this.fieldService.refreshAllFields();
                
                // Attendre que le changement de détection s'exécute avant de passer en mode READ
                setTimeout(() => {
                    const modifiedFields = this.fieldService.getFields().filter(f => f.isDirty());
                    modifiedFields.forEach(f => f.commit());
                    
                    this.configCmp.refreshData();
            
                    this.changeDetectorRef.markForCheck();
                    this.changeDetectorRef.detectChanges();
            
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('components.dyn-raw-detail-display.successSummary'),
                        detail: this.translate.instant('components.dyn-raw-detail-display.savedDetail')
                    });
                }, 0);
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('components.dyn-raw-detail-display.errorSummary'),
                    detail: this.translate.instant('components.dyn-raw-detail-display.saveFailedDetail', {message: error.message})
                });
            }
        });
    }

    getNbModif(): number {
        return this.fieldService.getFields().filter(f => {
            try {
                return f.isDirty();
            } catch (e) {
                console.log(f.field);
            }
            return false;
        }).length +
            this.fieldService.getFieldTags()
                .filter(f => f.isDirty())
                .length +
            (this.fieldService.getReassignedCustomer() != null ? 1 : 0);
    }

    getFieldDataNotFound() {
        return this.fieldService.getFields()
            .filter(f => f.fieldDataNotFound)
            .map(f => {
                const name = f.fiedTable.label || `${f.fiedTable.table}>${f.fiedTable.code}`;
                return `La valeur du champ '${name}' n'a pas été trouvé dans la liste de référence. `;
            });
    }


    private loadData(): void {
        const query = this.buildQuery(this.configCmp);
        const tableCodes = this.getTableCodes(this.configCmp.dataView);

        forkJoin({
            data: this.engineDataQueryService.getQueryDataContentByDataview(
                query,
                this.configCmp.datastore,
                this.configCmp.partner,
                this.configCmp.dataView.id
            ),
            tables: this.tableResourceService.getTablesUsingGET(
                this.configCmp.datastore,
                tableCodes
            )
        }).subscribe({
            next: ({ data, tables }) => {
                this.data = data;
                this.tables = tables;
                this.fieldService.data = data;
                this.fieldService.tables = tables;
                this.fieldService.refreshAllFields();
                this.changeDetectorRef.markForCheck();
                this.changeDetectorRef.detectChanges();
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('components.dyn-raw-detail-display.errorSummary'),
                    detail: this.translate.instant('components.dyn-raw-detail-display.loadErrorDetail')
                });
            }
        });
    }

    private buildQuery(config: RawDetailInfoInterface): any {
        const query = JSON.parse(JSON.stringify(config.currentQueryFilterCache));
        const table = config.dataView.table.tableName;
        query.constraint = {
            '@type': 'ComparisonConstraint',
            operand1: { '@type': 'PropertyValue', propertyName: `${table}.id` } as PropertyValue,
            operand2: { '@type': 'Value', typeValue: 'DOUBLE', value: config.id } as Value,
            operator: 'EQUALS'
        } as ComparisonConstraint;
        return query;
    }

    private getTableCodes(dataview: DataViewDto): string[] {
        const tableCodes = [dataview.table.tableName];
        const getJoinedTables = (joins: TableJointure[]): void => {
            if (!joins) return;

            joins.forEach(join => {
                tableCodes.push(join.tableName);
                if (join.join) {
                    getJoinedTables(join.join);
                }
            });
        };

        getJoinedTables(dataview.table.join);
        return [...new Set(tableCodes)];
    }

    onTabChange(event){
        const tabId = this.tabMapping[event.index];
        if(tabId) {
            this.dataLoaded[tabId] = true;
        }
    }

    private initTabMapping() {
        // Order must match the <p-tabPanel> order in the template
        this.tabMapping = ['details'];

        if (this.isPersonalDataview) {
            // For personal dataviews: details, orders, campaigns, consents, activities, ltv, history
            this.tabMapping.push('orders', 'campaigns', 'consents', 'activities', 'ltv', 'history');
        } else if (this.isCommandDataview) {
            // For command dataviews: details, campaigns, ltv, history
            this.tabMapping.push('campaigns', 'ltv', 'history');
        }

        this.tabMapping.forEach(tabId => {
            this.dataLoaded[tabId] = tabId === 'details';
        });
    }

    public extractTableIdsForHistory():{[key:string]:number}{

        const tableNames = this.isCommandDataview ? this.tablesByDataviewType['command'] : (this.isPersonalDataview ? this.tablesByDataviewType['personal'] : [])
        const modifications: {[key: string]: number} = {};

        for(let tableName of tableNames){
            if(this.data.mainData.rows[`${tableName}.id`]){
                modifications[tableName] = this.data.mainData.rows[`${tableName}.id`] as number;
            }
        }
        return modifications;
    }
}

